import React, { useState, useMemo } from 'react';
import { formatValue, formatConfidence } from '../../utils/formatters';
import ContentDescriptionModal from '../ContentDescriptionModal';

// Normalize URL for comparison (remove trailing slash, lowercase, etc.)
const normalizeUrl = (url) => {
    if (!url) return '';
    try {
        // Remove trailing slash, convert to lowercase, and remove fragment
        const urlObj = new URL(url);
        let normalized = urlObj.origin + urlObj.pathname;
        if (normalized.endsWith('/') && normalized.length > 1) {
            normalized = normalized.slice(0, -1);
        }
        return normalized.toLowerCase();
    } catch (e) {
        // If URL parsing fails, just normalize the string
        let normalized = url.toLowerCase().trim();
        if (normalized.endsWith('/') && normalized.length > 1) {
            normalized = normalized.slice(0, -1);
        }
        // Remove fragment
        const hashIndex = normalized.indexOf('#');
        if (hashIndex !== -1) {
            normalized = normalized.substring(0, hashIndex);
        }
        return normalized;
    }
};

const PagesSection = ({ pagesMetadata, urlData, imagesData, videosData, filesData, socialMediaData, onViewImages, onViewVideos, onViewFiles, websiteGraph, formsData, onViewForm, contactFormsData, pageFeatures }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [contentDescriptionModal, setContentDescriptionModal] = useState({ isOpen: false, page: null });

    // Combine pages metadata with content data
    const enrichedPages = useMemo(() => {
        if (!pagesMetadata || !Array.isArray(pagesMetadata) || pagesMetadata.length === 0) {
            return [];
        }

        const contactFormUrls = urlData?.contactFormUrls || [];
        const blogUrls = urlData?.blogUrls || [];
        const productGalleryUrls = urlData?.productGalleryUrls || [];
        const googleMapsUrls = urlData?.googleMapsUrls || [];
        const thirdPartyIntegrationsUrls = urlData?.thirdPartyIntegrationsUrls || {};
        const images = imagesData?.images || [];
        const videos = videosData?.videos || [];
        const files = filesData?.files || [];
        const forms = formsData || [];
        const contactForms = contactFormsData || [];
        const pageFeaturesArray = pageFeatures || [];
        const socialMediaPlatforms = socialMediaData?.platforms || [];

        // Create a map of normalized URL and page_hash to page features
        // We'll match by URL first, then by page_hash if available
        const urlToPageFeatures = {};
        const hashToPageFeatures = {};
        pageFeaturesArray.forEach(feature => {
            if (feature.url) {
                const normalized = normalizeUrl(feature.url);
                if (!urlToPageFeatures[normalized]) {
                    urlToPageFeatures[normalized] = [];
                }
                urlToPageFeatures[normalized].push(feature);
            }
            if (feature.page_hash) {
                hashToPageFeatures[feature.page_hash] = feature;
            }
        });

        // Create normalized sets of URLs for quick lookup
        const contactFormUrlSet = new Set(contactFormUrls.map(normalizeUrl));
        const blogUrlSet = new Set(blogUrls.map(normalizeUrl));
        const productGalleryUrlSet = new Set(productGalleryUrls.map(normalizeUrl));
        const googleMapsUrlSet = new Set(googleMapsUrls.map(normalizeUrl));
        
        // Create a map of normalized URL to third party integrations
        const urlToThirdPartyIntegrations = {};
        Object.entries(thirdPartyIntegrationsUrls).forEach(([integrationName, urls]) => {
            if (Array.isArray(urls)) {
                urls.forEach(url => {
                    const normalized = normalizeUrl(url);
                    if (!urlToThirdPartyIntegrations[normalized]) {
                        urlToThirdPartyIntegrations[normalized] = [];
                    }
                    urlToThirdPartyIntegrations[normalized].push(integrationName);
                });
            }
        });

        // Create a map of normalized page URL to images
        // Images have a 'pages' array that contains the pages where the image is used
        const urlToImages = {};
        images.forEach(image => {
            if (image.pages && Array.isArray(image.pages)) {
                image.pages.forEach(page => {
                    if (page.url) {
                        const normalized = normalizeUrl(page.url);
                        if (!urlToImages[normalized]) {
                            urlToImages[normalized] = [];
                        }
                        urlToImages[normalized].push(image);
                    }
                });
            }
        });

        // Create a map of normalized page URL to videos
        const urlToVideos = {};
        videos.forEach(video => {
            if (video.pages && Array.isArray(video.pages)) {
                video.pages.forEach(page => {
                    if (page.url) {
                        const normalized = normalizeUrl(page.url);
                        if (!urlToVideos[normalized]) {
                            urlToVideos[normalized] = [];
                        }
                        urlToVideos[normalized].push(video);
                    }
                });
            }
        });

        // Create a map of normalized page URL to files
        const urlToFiles = {};
        files.forEach(file => {
            if (file.pages && Array.isArray(file.pages)) {
                file.pages.forEach(page => {
                    if (page.url) {
                        const normalized = normalizeUrl(page.url);
                        if (!urlToFiles[normalized]) {
                            urlToFiles[normalized] = [];
                        }
                        urlToFiles[normalized].push(file);
                    }
                });
            }
        });

        // Create a map of normalized page URL to social media platforms
        const urlToSocialMedia = {};
        socialMediaPlatforms.forEach(platform => {
            const foundOn = platform.found_on || [];
            foundOn.forEach(pageUrl => {
                if (pageUrl) {
                    const normalized = normalizeUrl(pageUrl);
                    if (!urlToSocialMedia[normalized]) {
                        urlToSocialMedia[normalized] = [];
                    }
                    urlToSocialMedia[normalized].push(platform);
                }
            });
        });

        // Create a map of normalized page URL to forms
        const urlToForms = {};
        forms.forEach(form => {
            if (form.url) {
                const normalized = normalizeUrl(form.url);
                if (!urlToForms[normalized]) {
                    urlToForms[normalized] = [];
                }
                urlToForms[normalized].push(form);
            }
        });

        // Create a map of normalized page URL to contact forms
        const urlToContactForms = {};
        contactForms.forEach(form => {
            if (form.url) {
                const normalized = normalizeUrl(form.url);
                if (!urlToContactForms[normalized]) {
                    urlToContactForms[normalized] = [];
                }
                urlToContactForms[normalized].push(form);
            }
        });

        return pagesMetadata.map(page => {
            const pageUrl = normalizeUrl(page.url || '');
            
            // Try to find matching page features by URL or page_hash
            let pageFeature = null;
            if (page.page_hash && hashToPageFeatures[page.page_hash]) {
                pageFeature = hashToPageFeatures[page.page_hash];
            } else if (urlToPageFeatures[pageUrl] && urlToPageFeatures[pageUrl].length > 0) {
                // If multiple features match the URL, use the first one
                pageFeature = urlToPageFeatures[pageUrl][0];
            }
            
            // Merge page features data, prioritizing page_features over urlData when both exist
            const hasGoogleMapsFromFeatures = pageFeature?.has_google_maps;
            const hasGoogleMapsFromUrlData = googleMapsUrlSet.has(pageUrl);
            const hasGoogleMaps = hasGoogleMapsFromFeatures !== undefined ? hasGoogleMapsFromFeatures : hasGoogleMapsFromUrlData;
            
            const thirdPartyIntegrationsFromFeatures = pageFeature?.third_party_integrations || [];
            const thirdPartyIntegrationsFromUrlData = urlToThirdPartyIntegrations[pageUrl] || [];
            // Combine both sources, removing duplicates
            const allThirdPartyIntegrations = [...new Set([...thirdPartyIntegrationsFromFeatures, ...thirdPartyIntegrationsFromUrlData])];
            
            const hasBlogFromFeatures = pageFeature?.is_part_of_blog;
            const hasBlogFromUrlData = blogUrlSet.has(pageUrl);
            const hasBlog = hasBlogFromFeatures !== undefined ? hasBlogFromFeatures : hasBlogFromUrlData;
            
            return {
                ...page,
                hasContactForm: contactFormUrlSet.has(pageUrl),
                hasBlog: hasBlog,
                hasProductGallery: productGalleryUrlSet.has(pageUrl),
                hasGoogleMaps: hasGoogleMaps,
                thirdPartyIntegrations: allThirdPartyIntegrations,
                images: urlToImages[pageUrl] || [],
                videos: urlToVideos[pageUrl] || [],
                files: urlToFiles[pageUrl] || [],
                socialMedia: urlToSocialMedia[pageUrl] || [],
                forms: urlToForms[pageUrl] || [],
                contactForms: urlToContactForms[pageUrl] || [],
                // Add page_features specific data
                isPartOfShop: pageFeature?.is_part_of_shop || false,
                hasCta: pageFeature?.has_cta || false,
                pageLanguage: pageFeature?.page_language || null
            };
        });
    }, [pagesMetadata, urlData, imagesData, videosData, filesData, socialMediaData, formsData, contactFormsData, pageFeatures]);

    if (!enrichedPages || enrichedPages.length === 0) {
        return null;
    }

    const visiblePages = isExpanded ? enrichedPages : enrichedPages.slice(0, 3);
    const hasMore = enrichedPages.length > 3;

    // Handle base64 encoded PNG for website graph
    const graphImageSrc = websiteGraph 
        ? (websiteGraph.startsWith('data:image') 
            ? websiteGraph 
            : `data:image/png;base64,${websiteGraph}`)
        : null;

    return (
        <div style={{ marginBottom: '30px' }}>
            {/* Website Graph */}
            {graphImageSrc && (
                <div style={{ marginBottom: '30px' }}>
                    <h4 style={{ marginBottom: '15px', color: '#e0e0e0', fontWeight: '600' }}>
                        Website Graph
                    </h4>
                    <div style={{
                        backgroundColor: '#2d2d2d',
                        border: '1px solid #404040',
                        borderRadius: '6px',
                        padding: '20px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <img
                            src={graphImageSrc}
                            alt="Website Graph"
                            style={{
                                maxWidth: '100%',
                                height: 'auto',
                                borderRadius: '8px',
                                border: '1px solid rgba(100, 181, 246, 0.12)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                            }}
                        />
                    </div>
                </div>
            )}

            <h4 style={{ marginBottom: '15px', color: '#e0e0e0', fontWeight: '600' }}>
                Pages ({enrichedPages.length} {enrichedPages.length === 1 ? 'page' : 'pages'})
            </h4>
            <div style={{
                backgroundColor: '#2d2d2d',
                border: '1px solid #404040',
                borderRadius: '6px',
                padding: '20px'
            }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {visiblePages.map((page, index) => (
                        <div
                            key={index}
                            style={{
                                backgroundColor: '#262626',
                                border: '1px solid #373737',
                                borderRadius: '4px',
                                padding: '16px'
                            }}
                        >
                            <div style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#e0e0e0',
                                marginBottom: '8px',
                                wordBreak: 'break-word'
                            }}>
                                {page.title || page.url || `Page ${index + 1}`}
                            </div>
                            <div style={{
                                fontSize: '12px',
                                color: '#a0a0a0',
                                marginBottom: '12px',
                                wordBreak: 'break-all'
                            }}>
                                <a
                                    href={page.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: '#60a5fa',
                                        textDecoration: 'none'
                                    }}
                                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                >
                                    {page.url}
                                </a>
                            </div>

                            {/* Features badges */}
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px',
                                marginBottom: '12px'
                            }}>
                                {page.hasContactForm && (
                                    <span style={{
                                        display: 'inline-block',
                                        backgroundColor: '#1e3a5f',
                                        color: '#60a5fa',
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '500'
                                    }}>
                                        Contact Form
                                    </span>
                                )}
                                {page.hasBlog && (
                                    <span style={{
                                        display: 'inline-block',
                                        backgroundColor: '#1e3a5f',
                                        color: '#60a5fa',
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '500'
                                    }}>
                                        Blog
                                    </span>
                                )}
                                {page.hasProductGallery && (
                                    <span style={{
                                        display: 'inline-block',
                                        backgroundColor: '#1e3a5f',
                                        color: '#60a5fa',
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '500'
                                    }}>
                                        Product Gallery
                                    </span>
                                )}
                                {page.hasGoogleMaps && (
                                    <span style={{
                                        display: 'inline-block',
                                        backgroundColor: '#1e3a5f',
                                        color: '#60a5fa',
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '500'
                                    }}>
                                        Google Maps
                                    </span>
                                )}
                                {page.isPartOfShop && (
                                    <span style={{
                                        display: 'inline-block',
                                        backgroundColor: '#1e3a3a',
                                        color: '#34d399',
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '500'
                                    }}>
                                        Shop
                                    </span>
                                )}
                                {page.hasCta && (
                                    <span style={{
                                        display: 'inline-block',
                                        backgroundColor: '#3a1e2e',
                                        color: '#f472b6',
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '500'
                                    }}>
                                        CTA
                                    </span>
                                )}
                                {page.pageLanguage && (
                                    <span style={{
                                        display: 'inline-block',
                                        backgroundColor: '#2d2d2d',
                                        color: '#e0e0e0',
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '500'
                                    }}>
                                        Language: {page.pageLanguage}
                                    </span>
                                )}
                                {page.forms && page.forms.length > 0 && (
                                    <span style={{
                                        display: 'inline-block',
                                        backgroundColor: '#2d1e3a',
                                        color: '#a78bfa',
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '500'
                                    }}>
                                        {page.forms.length} {page.forms.length === 1 ? 'Form' : 'Forms'}
                                    </span>
                                )}
                                {page.thirdPartyIntegrations.length > 0 && (
                                    <span style={{
                                        display: 'inline-block',
                                        backgroundColor: '#3a2e1e',
                                        color: '#fbbf24',
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '500'
                                    }}>
                                        {page.thirdPartyIntegrations.length} 3rd Party
                                    </span>
                                )}
                                {page.topic && (
                                    <span style={{
                                        display: 'inline-block',
                                        backgroundColor: '#2d2d2d',
                                        color: '#e0e0e0',
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '500'
                                    }}>
                                        Topic: {formatValue(page.topic)}
                                        {page.topic_confidence !== undefined && page.topic_confidence !== null && (
                                            <> ({formatConfidence(page.topic_confidence)})</>
                                        )}
                                    </span>
                                )}
                            </div>

                            {/* Additional details */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                                gap: '12px',
                                marginTop: '12px'
                            }}>
                                {page.internal_links_to && Array.isArray(page.internal_links_to) && (
                                    <div style={{
                                        backgroundColor: '#0c0c0c',
                                        border: '1px solid rgba(100, 181, 246, 0.08)',
                                        borderRadius: '6px',
                                        padding: '8px'
                                    }}>
                                        <div style={{
                                            fontSize: '10px',
                                            color: '#909090',
                                            textTransform: 'uppercase',
                                            marginBottom: '4px',
                                            fontWeight: '600',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Internal Links
                                        </div>
                                        <div style={{
                                            fontSize: '13px',
                                            color: '#e0e0e0',
                                            fontWeight: '600'
                                        }}>
                                            {page.internal_links_to.length}
                                        </div>
                                    </div>
                                )}
                                {page.external_links && Array.isArray(page.external_links) && (
                                    <div style={{
                                        backgroundColor: '#0c0c0c',
                                        border: '1px solid rgba(100, 181, 246, 0.08)',
                                        borderRadius: '6px',
                                        padding: '8px'
                                    }}>
                                        <div style={{
                                            fontSize: '10px',
                                            color: '#909090',
                                            textTransform: 'uppercase',
                                            marginBottom: '4px',
                                            fontWeight: '600',
                                            letterSpacing: '0.5px'
                                        }}>
                                            External Links
                                        </div>
                                        <div style={{
                                            fontSize: '13px',
                                            color: '#e0e0e0',
                                            fontWeight: '600'
                                        }}>
                                            {page.external_links.length}
                                        </div>
                                    </div>
                                )}
                                {page.images.length > 0 && (
                                    <div 
                                        style={{
                                            backgroundColor: '#0c0c0c',
                                            border: '1px solid rgba(100, 181, 246, 0.08)',
                                            borderRadius: '6px',
                                            padding: '8px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onClick={() => onViewImages && onViewImages(page.images)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#1a1a1a';
                                            e.currentTarget.style.borderColor = 'rgba(100, 181, 246, 0.2)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#0c0c0c';
                                            e.currentTarget.style.borderColor = 'rgba(100, 181, 246, 0.08)';
                                        }}
                                    >
                                        <div style={{
                                            fontSize: '10px',
                                            color: '#909090',
                                            textTransform: 'uppercase',
                                            marginBottom: '4px',
                                            fontWeight: '600',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Images
                                        </div>
                                        <div style={{
                                            fontSize: '13px',
                                            color: '#e0e0e0',
                                            fontWeight: '600'
                                        }}>
                                            {page.images.length}
                                        </div>
                                    </div>
                                )}
                                {page.videos && page.videos.length > 0 && (
                                    <div 
                                        style={{
                                            backgroundColor: '#0c0c0c',
                                            border: '1px solid rgba(239, 68, 68, 0.08)',
                                            borderRadius: '6px',
                                            padding: '8px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onClick={() => onViewVideos && onViewVideos(page.videos)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#1a1a1a';
                                            e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.2)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#0c0c0c';
                                            e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.08)';
                                        }}
                                    >
                                        <div style={{
                                            fontSize: '10px',
                                            color: '#909090',
                                            textTransform: 'uppercase',
                                            marginBottom: '4px',
                                            fontWeight: '600',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Videos
                                        </div>
                                        <div style={{
                                            fontSize: '13px',
                                            color: '#e0e0e0',
                                            fontWeight: '600'
                                        }}>
                                            {page.videos.length}
                                        </div>
                                    </div>
                                )}
                                {page.files && page.files.length > 0 && (
                                    <div 
                                        style={{
                                            backgroundColor: '#0c0c0c',
                                            border: '1px solid rgba(34, 211, 153, 0.08)',
                                            borderRadius: '6px',
                                            padding: '8px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onClick={() => onViewFiles && onViewFiles(page.files)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#1a1a1a';
                                            e.currentTarget.style.borderColor = 'rgba(34, 211, 153, 0.2)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#0c0c0c';
                                            e.currentTarget.style.borderColor = 'rgba(34, 211, 153, 0.08)';
                                        }}
                                    >
                                        <div style={{
                                            fontSize: '10px',
                                            color: '#909090',
                                            textTransform: 'uppercase',
                                            marginBottom: '4px',
                                            fontWeight: '600',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Files
                                        </div>
                                        <div style={{
                                            fontSize: '13px',
                                            color: '#e0e0e0',
                                            fontWeight: '600'
                                        }}>
                                            {page.files.length}
                                        </div>
                                    </div>
                                )}
                                {page.socialMedia && page.socialMedia.length > 0 && (
                                    <div 
                                        style={{
                                            backgroundColor: '#0c0c0c',
                                            border: '1px solid rgba(168, 85, 247, 0.08)',
                                            borderRadius: '6px',
                                            padding: '8px',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <div style={{
                                            fontSize: '10px',
                                            color: '#909090',
                                            textTransform: 'uppercase',
                                            marginBottom: '4px',
                                            fontWeight: '600',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Social Media
                                        </div>
                                        <div style={{
                                            fontSize: '13px',
                                            color: '#e0e0e0',
                                            fontWeight: '600'
                                        }}>
                                            {page.socialMedia.length}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Forms list */}
                            {page.forms && page.forms.length > 0 && (
                                <div style={{
                                    marginTop: '12px',
                                    paddingTop: '12px',
                                    borderTop: '1px solid rgba(167, 139, 250, 0.12)'
                                }}>
                                    <div style={{
                                        fontSize: '11px',
                                        color: '#909090',
                                        textTransform: 'uppercase',
                                        marginBottom: '8px',
                                        fontWeight: '600',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Forms ({page.forms.length})
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px'
                                    }}>
                                        {page.forms.map((form, formIndex) => {
                                            const fields = form.fields || [];
                                            const totalFields = fields.length;
                                            const requiredFields = fields.filter(f => f.required).length;
                                            const visibleFields = fields.filter(f => f.type !== 'hidden').length;
                                            
                                            return (
                                                <div
                                                    key={formIndex}
                                                    onClick={() => onViewForm && onViewForm(form)}
                                                    style={{
                                                        backgroundColor: '#181818',
                                                        border: '1px solid rgba(167, 139, 250, 0.12)',
                                                        borderRadius: '6px',
                                                        padding: '12px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#1f1f1f';
                                                        e.currentTarget.style.borderColor = 'rgba(167, 139, 250, 0.25)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#181818';
                                                        e.currentTarget.style.borderColor = 'rgba(167, 139, 250, 0.12)';
                                                    }}
                                                >
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'flex-start',
                                                        marginBottom: '8px',
                                                        gap: '8px',
                                                        flexWrap: 'wrap'
                                                    }}>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{
                                                                fontSize: '13px',
                                                                fontWeight: '600',
                                                                color: '#e0e0e0',
                                                                marginBottom: '4px',
                                                                wordBreak: 'break-word'
                                                            }}>
                                                                {form.form_type ? formatValue(form.form_type) : 'Form'}
                                                            </div>
                                                            {form.method && (
                                                                <div style={{
                                                                    fontSize: '11px',
                                                                    color: '#a0a0a0',
                                                                    textTransform: 'uppercase'
                                                                }}>
                                                                    {form.method}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div style={{
                                                            display: 'flex',
                                                            gap: '6px',
                                                            flexWrap: 'wrap'
                                                        }}>
                                                            {form.form_type && (
                                                                <span style={{
                                                                    display: 'inline-block',
                                                                    backgroundColor: '#2d1e3a',
                                                                    color: '#a78bfa',
                                                                    padding: '3px 8px',
                                                                    borderRadius: '8px',
                                                                    fontSize: '10px',
                                                                    fontWeight: '500'
                                                                }}>
                                                                    {formatValue(form.form_type)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
                                                        gap: '6px',
                                                        fontSize: '11px',
                                                        color: '#909090'
                                                    }}>
                                                        <div>
                                                            <span style={{ color: '#a78bfa' }}>{totalFields}</span> fields
                                                        </div>
                                                        {requiredFields > 0 && (
                                                            <div>
                                                                <span style={{ color: '#f87171' }}>{requiredFields}</span> required
                                                            </div>
                                                        )}
                                                        <div>
                                                            <span style={{ color: '#34d399' }}>{visibleFields}</span> visible
                                                        </div>
                                                    </div>
                                                    <div style={{
                                                        marginTop: '8px',
                                                        fontSize: '10px',
                                                        color: '#909090',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        Click to view details
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Contact Forms list */}
                            {page.contactForms && page.contactForms.length > 0 && (
                                <div style={{
                                    marginTop: '12px',
                                    paddingTop: '12px',
                                    borderTop: '1px solid rgba(60, 165, 250, 0.12)'
                                }}>
                                    <div style={{
                                        fontSize: '11px',
                                        color: '#909090',
                                        textTransform: 'uppercase',
                                        marginBottom: '8px',
                                        fontWeight: '600',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Contact Forms ({page.contactForms.length})
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px'
                                    }}>
                                        {page.contactForms.map((form, formIndex) => {
                                            const fields = form.fields || [];
                                            const totalFields = fields.length;
                                            const requiredFields = fields.filter(f => f.required).length;
                                            const visibleFields = fields.filter(f => f.type !== 'hidden').length;
                                            
                                            return (
                                                <div
                                                    key={formIndex}
                                                    onClick={() => onViewForm && onViewForm(form)}
                                                    style={{
                                                        backgroundColor: '#181818',
                                                        border: '1px solid rgba(60, 165, 250, 0.12)',
                                                        borderRadius: '6px',
                                                        padding: '12px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#1f1f1f';
                                                        e.currentTarget.style.borderColor = 'rgba(60, 165, 250, 0.25)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#181818';
                                                        e.currentTarget.style.borderColor = 'rgba(60, 165, 250, 0.12)';
                                                    }}
                                                >
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'flex-start',
                                                        marginBottom: '8px',
                                                        gap: '8px',
                                                        flexWrap: 'wrap'
                                                    }}>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{
                                                                fontSize: '13px',
                                                                fontWeight: '600',
                                                                color: '#e0e0e0',
                                                                marginBottom: '4px',
                                                                wordBreak: 'break-word'
                                                            }}>
                                                                {form.form_type ? formatValue(form.form_type) : 'Contact Form'}
                                                            </div>
                                                            {form.method && (
                                                                <div style={{
                                                                    fontSize: '11px',
                                                                    color: '#a0a0a0',
                                                                    textTransform: 'uppercase'
                                                                }}>
                                                                    {form.method}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div style={{
                                                            display: 'flex',
                                                            gap: '6px',
                                                            flexWrap: 'wrap'
                                                        }}>
                                                            {form.form_type && (
                                                                <span style={{
                                                                    display: 'inline-block',
                                                                    backgroundColor: '#1e3a5f',
                                                                    color: '#60a5fa',
                                                                    padding: '3px 8px',
                                                                    borderRadius: '8px',
                                                                    fontSize: '10px',
                                                                    fontWeight: '500'
                                                                }}>
                                                                    {formatValue(form.form_type)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
                                                        gap: '6px',
                                                        fontSize: '11px',
                                                        color: '#909090'
                                                    }}>
                                                        <div>
                                                            <span style={{ color: '#60a5fa' }}>{totalFields}</span> fields
                                                        </div>
                                                        {requiredFields > 0 && (
                                                            <div>
                                                                <span style={{ color: '#f87171' }}>{requiredFields}</span> required
                                                            </div>
                                                        )}
                                                        <div>
                                                            <span style={{ color: '#34d399' }}>{visibleFields}</span> visible
                                                        </div>
                                                    </div>
                                                    <div style={{
                                                        marginTop: '8px',
                                                        fontSize: '10px',
                                                        color: '#909090',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        Click to view details
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Social Media details */}
                            {page.socialMedia && page.socialMedia.length > 0 && (
                                <div style={{
                                    marginTop: '12px',
                                    paddingTop: '12px',
                                    borderTop: '1px solid rgba(168, 85, 247, 0.12)'
                                }}>
                                    <div style={{
                                        fontSize: '11px',
                                        color: '#909090',
                                        textTransform: 'uppercase',
                                        marginBottom: '8px',
                                        fontWeight: '600',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Social Media ({page.socialMedia.length})
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px'
                                    }}>
                                        {page.socialMedia.map((platform, platformIndex) => {
                                            const platformName = platform.platform || `Platform ${platformIndex + 1}`;
                                            const urls = platform.urls || [];
                                            
                                            return (
                                                <div
                                                    key={platformIndex}
                                                    style={{
                                                        backgroundColor: '#181818',
                                                        border: '1px solid rgba(168, 85, 247, 0.12)',
                                                        borderRadius: '6px',
                                                        padding: '12px',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <div style={{
                                                        fontSize: '13px',
                                                        fontWeight: '600',
                                                        color: '#e0e0e0',
                                                        marginBottom: '8px',
                                                        textTransform: 'capitalize'
                                                    }}>
                                                        {platformName}
                                                    </div>
                                                    {urls.length > 0 && (
                                                        <div style={{
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            gap: '6px'
                                                        }}>
                                                            {urls.map((url, urlIndex) => (
                                                                <a
                                                                    key={urlIndex}
                                                                    href={url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    style={{
                                                                        color: '#a78bfa',
                                                                        textDecoration: 'none',
                                                                        fontSize: '11px',
                                                                        padding: '4px 8px',
                                                                        backgroundColor: '#1f1f1f',
                                                                        borderRadius: '4px',
                                                                        border: '1px solid rgba(168, 85, 247, 0.2)',
                                                                        transition: 'all 0.2s ease',
                                                                        wordBreak: 'break-all',
                                                                        maxWidth: '100%'
                                                                    }}
                                                                    onMouseEnter={(e) => {
                                                                        e.target.style.backgroundColor = '#262626';
                                                                        e.target.style.textDecoration = 'underline';
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        e.target.style.backgroundColor = '#1f1f1f';
                                                                        e.target.style.textDecoration = 'none';
                                                                    }}
                                                                >
                                                                    {url.length > 50 ? url.substring(0, 50) + '...' : url}
                                                                </a>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Third party integrations details */}
                            {page.thirdPartyIntegrations.length > 0 && (
                                <div style={{
                                    marginTop: '12px',
                                    paddingTop: '12px',
                                    borderTop: '1px solid rgba(100, 181, 246, 0.12)'
                                }}>
                                    <div style={{
                                        fontSize: '11px',
                                        color: '#909090',
                                        textTransform: 'uppercase',
                                        marginBottom: '6px',
                                        fontWeight: '600',
                                        letterSpacing: '0.5px'
                                    }}>
                                        3rd Party Integrations
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '6px'
                                    }}>
                                        {page.thirdPartyIntegrations.map((integration, idx) => (
                                            <span
                                                key={idx}
                                                style={{
                                                    display: 'inline-block',
                                                    backgroundColor: '#3a2e1e',
                                                    color: '#fbbf24',
                                                    padding: '3px 8px',
                                                    borderRadius: '8px',
                                                    fontSize: '10px',
                                                    fontWeight: '500'
                                                }}
                                            >
                                                {integration}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Metadata Tags */}
                            {page.metadata_tags && (
                                <div style={{
                                    marginTop: '12px',
                                    paddingTop: '12px',
                                    borderTop: '1px solid rgba(100, 181, 246, 0.12)'
                                }}>
                                    <div style={{
                                        fontSize: '11px',
                                        color: '#909090',
                                        textTransform: 'uppercase',
                                        marginBottom: '12px',
                                        fontWeight: '600',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Metadata Tags
                                    </div>

                                    {page.metadata_tags.meta_tags && Object.keys(page.metadata_tags.meta_tags).length > 0 && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <div style={{
                                                fontSize: '10px',
                                                color: '#a0a0a0',
                                                textTransform: 'uppercase',
                                                marginBottom: '6px',
                                                fontWeight: '600',
                                                letterSpacing: '0.5px'
                                            }}>
                                                Meta Tags
                                            </div>
                                            <div style={{
                                                backgroundColor: '#0c0c0c',
                                                border: '1px solid rgba(100, 181, 246, 0.08)',
                                                borderRadius: '6px',
                                                padding: '10px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '4px'
                                            }}>
                                                {Object.entries(page.metadata_tags.meta_tags).map(([key, value]) => (
                                                    <div key={key} style={{
                                                        display: 'flex',
                                                        gap: '8px',
                                                        fontSize: '11px'
                                                    }}>
                                                        <span style={{
                                                            color: '#909090',
                                                            fontWeight: '600',
                                                            minWidth: '100px'
                                                        }}>
                                                            {key}:
                                                        </span>
                                                        <span style={{
                                                            color: '#d0d0d0',
                                                            wordBreak: 'break-word',
                                                            flex: 1
                                                        }}>
                                                            {formatValue(value) || '(empty)'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {page.metadata_tags.open_graph && Object.keys(page.metadata_tags.open_graph).length > 0 && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <div style={{
                                                fontSize: '10px',
                                                color: '#a0a0a0',
                                                textTransform: 'uppercase',
                                                marginBottom: '6px',
                                                fontWeight: '600',
                                                letterSpacing: '0.5px'
                                            }}>
                                                Open Graph
                                            </div>
                                            <div style={{
                                                backgroundColor: '#0c0c0c',
                                                border: '1px solid rgba(100, 181, 246, 0.08)',
                                                borderRadius: '6px',
                                                padding: '10px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '4px'
                                            }}>
                                                {Object.entries(page.metadata_tags.open_graph).map(([key, value]) => (
                                                    <div key={key} style={{
                                                        display: 'flex',
                                                        gap: '8px',
                                                        fontSize: '11px'
                                                    }}>
                                                        <span style={{
                                                            color: '#909090',
                                                            fontWeight: '600',
                                                            minWidth: '100px'
                                                        }}>
                                                            {key}:
                                                        </span>
                                                        <span style={{
                                                            color: '#d0d0d0',
                                                            wordBreak: 'break-word',
                                                            flex: 1
                                                        }}>
                                                            {formatValue(value) || '(empty)'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {page.metadata_tags.twitter_card && Object.keys(page.metadata_tags.twitter_card).length > 0 && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <div style={{
                                                fontSize: '10px',
                                                color: '#a0a0a0',
                                                textTransform: 'uppercase',
                                                marginBottom: '6px',
                                                fontWeight: '600',
                                                letterSpacing: '0.5px'
                                            }}>
                                                Twitter Card
                                            </div>
                                            <div style={{
                                                backgroundColor: '#0c0c0c',
                                                border: '1px solid rgba(100, 181, 246, 0.08)',
                                                borderRadius: '6px',
                                                padding: '10px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '4px'
                                            }}>
                                                {Object.entries(page.metadata_tags.twitter_card).map(([key, value]) => (
                                                    <div key={key} style={{
                                                        display: 'flex',
                                                        gap: '8px',
                                                        fontSize: '11px'
                                                    }}>
                                                        <span style={{
                                                            color: '#909090',
                                                            fontWeight: '600',
                                                            minWidth: '100px'
                                                        }}>
                                                            {key}:
                                                        </span>
                                                        <span style={{
                                                            color: '#d0d0d0',
                                                            wordBreak: 'break-word',
                                                            flex: 1
                                                        }}>
                                                            {formatValue(value) || '(empty)'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {page.metadata_tags.canonical_url && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <div style={{
                                                fontSize: '10px',
                                                color: '#a0a0a0',
                                                textTransform: 'uppercase',
                                                marginBottom: '6px',
                                                fontWeight: '600',
                                                letterSpacing: '0.5px'
                                            }}>
                                                Canonical URL
                                            </div>
                                            <a
                                                href={page.metadata_tags.canonical_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    fontSize: '11px',
                                                    color: '#60a5fa',
                                                    textDecoration: 'none',
                                                    wordBreak: 'break-all',
                                                    display: 'block'
                                                }}
                                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                            >
                                                {page.metadata_tags.canonical_url}
                                            </a>
                                        </div>
                                    )}

                                    {page.metadata_tags.hreflang && Array.isArray(page.metadata_tags.hreflang) && page.metadata_tags.hreflang.length > 0 && (
                                        <div>
                                            <div style={{
                                                fontSize: '10px',
                                                color: '#a0a0a0',
                                                textTransform: 'uppercase',
                                                marginBottom: '6px',
                                                fontWeight: '600',
                                                letterSpacing: '0.5px'
                                            }}>
                                                Hreflang
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '4px'
                                            }}>
                                                {page.metadata_tags.hreflang.map((item, itemIndex) => (
                                                    <div key={itemIndex} style={{
                                                        backgroundColor: '#0c0c0c',
                                                        border: '1px solid rgba(100, 181, 246, 0.08)',
                                                        borderRadius: '6px',
                                                        padding: '8px 10px',
                                                        display: 'flex',
                                                        gap: '8px',
                                                        fontSize: '11px'
                                                    }}>
                                                        <span style={{
                                                            color: '#909090',
                                                            fontWeight: '600',
                                                            minWidth: '50px'
                                                        }}>
                                                            {item.lang || '—'}:
                                                        </span>
                                                        <a
                                                            href={item.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                color: '#60a5fa',
                                                                textDecoration: 'none',
                                                                wordBreak: 'break-all',
                                                                flex: 1
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                        >
                                                            {item.url}
                                                        </a>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Content Description Button */}
                            {page.content_description && (
                                <div style={{
                                    marginTop: '12px',
                                    paddingTop: '12px',
                                    borderTop: '1px solid rgba(100, 181, 246, 0.12)'
                                }}>
                                    <button
                                        onClick={() => setContentDescriptionModal({ isOpen: true, page })}
                                        style={{
                                            backgroundColor: '#6366f1',
                                            color: '#ffffff',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '8px 16px',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)',
                                            transition: 'all 0.2s ease',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = '#4f46e5';
                                            e.target.style.boxShadow = '0 6px 14px rgba(99, 102, 241, 0.45)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = '#6366f1';
                                            e.target.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.35)';
                                        }}
                                    >
                                        📄 View Content Description
                                    </button>
                                </div>
                            )}

                        </div>
                    ))}
                </div>
                {hasMore && (
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        style={{
                            marginTop: '16px',
                            padding: '8px 16px',
                            backgroundColor: '#3d3d3d',
                            border: '1px solid #505050',
                            borderRadius: '4px',
                            color: '#e0e0e0',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            alignSelf: 'flex-start'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#4d4d4d';
                            e.target.style.borderColor = '#606060';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#3d3d3d';
                            e.target.style.borderColor = '#505050';
                        }}
                    >
                        {isExpanded ? `Show Less` : `Show More (${enrichedPages.length - 3} more)`}
                    </button>
                )}
            </div>

            {/* Content Description Modal */}
            <ContentDescriptionModal
                isOpen={contentDescriptionModal.isOpen}
                onClose={() => setContentDescriptionModal({ isOpen: false, page: null })}
                contentDescription={contentDescriptionModal.page?.content_description}
                pageTitle={contentDescriptionModal.page?.title}
                pageUrl={contentDescriptionModal.page?.url}
            />
        </div>
    );
};

export default PagesSection;

